/**
 * お気に入り機能 - localStorage ベースの実装
 *
 * 機能:
 * - 商品をお気に入りに追加/削除
 * - お気に入り一覧の表示
 * - お気に入り件数のバッジ表示
 */

class FavoritesManager {
  constructor() {
    this.storageKey = 'shopify_favorites';
    this.favorites = this.loadFavorites();
    this.init();
  }

  init() {
    this.updateBadge();
    this.bindEvents();
  }

  loadFavorites() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load favorites:', e);
      return [];
    }
  }

  saveFavorites() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.favorites));
      this.updateBadge();
      this.dispatchEvent('favorites:updated');
    } catch (e) {
      console.error('Failed to save favorites:', e);
    }
  }

  addFavorite(product) {
    if (!this.isFavorite(product.id)) {
      this.favorites.push({
        id: product.id,
        handle: product.handle,
        title: product.title,
        image: product.image,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        url: product.url,
        addedAt: new Date().toISOString()
      });
      this.saveFavorites();
      return true;
    }
    return false;
  }

  removeFavorite(productId) {
    const index = this.favorites.findIndex(item => item.id === productId);
    if (index !== -1) {
      this.favorites.splice(index, 1);
      this.saveFavorites();
      return true;
    }
    return false;
  }

  toggleFavorite(product) {
    if (this.isFavorite(product.id)) {
      this.removeFavorite(product.id);
      return false;
    } else {
      this.addFavorite(product);
      return true;
    }
  }

  isFavorite(productId) {
    return this.favorites.some(item => item.id === productId);
  }

  getFavorites() {
    return this.favorites;
  }

  getCount() {
    return this.favorites.length;
  }

  clearAll() {
    this.favorites = [];
    this.saveFavorites();
  }

  updateBadge() {
    const badges = document.querySelectorAll('.favorites-count-badge');
    const count = this.getCount();

    badges.forEach(badge => {
      if (count > 0) {
        badge.textContent = count < 100 ? count : '99+';
        badge.style.display = '';
        badge.setAttribute('aria-hidden', 'false');
      } else {
        badge.style.display = 'none';
        badge.setAttribute('aria-hidden', 'true');
      }
    });
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-favorites-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const productData = this.getProductDataFromElement(toggleBtn);
        if (productData) {
          const isNowFavorite = this.toggleFavorite(productData);
          this.updateToggleButton(toggleBtn, isNowFavorite);
        }
      }
    });
  }

  getProductDataFromElement(element) {
    const id = element.dataset.productId;
    if (!id) return null;

    return {
      id: id,
      handle: element.dataset.productHandle || '',
      title: element.dataset.productTitle || '',
      image: element.dataset.productImage || '',
      price: element.dataset.productPrice || '',
      compareAtPrice: element.dataset.productComparePrice || '',
      url: element.dataset.productUrl || ''
    };
  }

  updateToggleButton(button, isFavorite) {
    button.classList.toggle('is-favorite', isFavorite);
    button.setAttribute('aria-pressed', isFavorite);

    const icon = button.querySelector('.favorites-icon');
    if (icon) {
      icon.classList.toggle('favorites-icon--filled', isFavorite);
    }

    const label = button.querySelector('.visually-hidden');
    if (label) {
      label.textContent = isFavorite
        ? window.favoritesStrings?.remove || 'お気に入りから削除'
        : window.favoritesStrings?.add || 'お気に入りに追加';
    }

    // 商品詳細ページのテキストボタン用
    const textLabel = button.querySelector('.product__favorites-text');
    if (textLabel) {
      textLabel.textContent = isFavorite
        ? window.favoritesStrings?.remove || 'お気に入りから削除'
        : window.favoritesStrings?.add || 'お気に入りに追加';
    }
  }

  dispatchEvent(eventName) {
    document.dispatchEvent(new CustomEvent(eventName, {
      detail: { favorites: this.favorites, count: this.getCount() }
    }));
  }
}

// お気に入りドロワー
class FavoritesDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.overlay = null;
    this.closeBtn = null;
    this.itemsContainer = null;
    this.emptyMessage = null;
  }

  connectedCallback() {
    this.drawer = this.querySelector('.favorites-drawer');
    this.overlay = this.querySelector('.favorites-drawer__overlay');
    this.closeBtn = this.querySelector('.favorites-drawer__close');
    this.itemsContainer = this.querySelector('.favorites-drawer__items');
    this.emptyMessage = this.querySelector('.favorites-drawer__empty');
    this.bindEvents();
  }

  bindEvents() {
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    document.addEventListener('favorites:updated', () => {
      if (this.drawer?.classList.contains('is-open')) {
        this.renderItems();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.renderItems();
    this.drawer?.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.drawer?.setAttribute('aria-hidden', 'false');
    this.trapFocus();
  }

  close() {
    this.drawer?.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.drawer?.setAttribute('aria-hidden', 'true');
  }

  trapFocus() {
    const focusableElements = this.drawer?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements?.length) {
      focusableElements[0].focus();
    }
  }

  renderItems() {
    if (!this.itemsContainer || !window.favoritesManager) return;

    const favorites = window.favoritesManager.getFavorites();

    if (favorites.length === 0) {
      this.itemsContainer.innerHTML = '';
      this.emptyMessage?.classList.remove('hidden');
      return;
    }

    this.emptyMessage?.classList.add('hidden');

    this.itemsContainer.innerHTML = favorites.map(item => `
      <div class="favorites-drawer__item" data-product-id="${item.id}">
        <a href="${item.url}" class="favorites-drawer__item-link">
          ${item.image ? `
            <div class="favorites-drawer__item-image">
              <img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="80" height="80">
            </div>
          ` : ''}
          <div class="favorites-drawer__item-info">
            <h3 class="favorites-drawer__item-title">${this.escapeHtml(item.title)}</h3>
            <div class="favorites-drawer__item-price">
              ${item.compareAtPrice && item.compareAtPrice !== item.price ? `
                <span class="favorites-drawer__compare-price">${item.compareAtPrice}</span>
              ` : ''}
              <span class="favorites-drawer__price">${item.price}</span>
            </div>
          </div>
        </a>
        <button
          type="button"
          class="favorites-drawer__remove"
          aria-label="${window.favoritesStrings?.remove || 'お気に入りから削除'}"
          data-remove-favorite="${item.id}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
          </svg>
        </button>
      </div>
    `).join('');

    // 削除ボタンのイベント
    this.itemsContainer.querySelectorAll('[data-remove-favorite]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = btn.dataset.removeFavorite;
        window.favoritesManager.removeFavorite(productId);
        this.renderItems();
      });
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// グローバルインスタンスを作成
document.addEventListener('DOMContentLoaded', () => {
  window.favoritesManager = new FavoritesManager();

  // お気に入りボタンの初期状態を設定
  document.querySelectorAll('[data-favorites-toggle]').forEach(btn => {
    const productId = btn.dataset.productId;
    if (productId && window.favoritesManager.isFavorite(productId)) {
      btn.classList.add('is-favorite');
      btn.setAttribute('aria-pressed', 'true');
      const icon = btn.querySelector('.favorites-icon');
      if (icon) icon.classList.add('favorites-icon--filled');
      // 商品詳細ページのテキストラベルを更新
      const textLabel = btn.querySelector('.product__favorites-text');
      if (textLabel) {
        textLabel.textContent = window.favoritesStrings?.remove || 'お気に入りから削除';
      }
    }
  });
});

// カスタム要素を登録
customElements.define('favorites-drawer', FavoritesDrawer);
